import React, {useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp } from '@mui/icons-material';
import BlockedBalanceCard from '../components/admin/BlockedBalanceCard';
import DashboardHeader from "../components/admin/DashboardHeader";
import AdminStatsGrid from "../components/admin/AdminStatsGrid";
import AdminSalesChart from "../components/admin/AdminSalesChart";
import AdminSidebar from "../components/admin/AdminSidebar";
import { useAuth } from "../contexts/AuthContext";
import { fetchTransactions } from "../services/transactionService";
import { fetchCompanyPos } from "../services/posService";
import { fetchDashboardStats, fetchTransactionChart, fetchAccountDetails } from "../services/dashboardService";
import { storageKeys } from "../config/environment";
// Données mock enrichies
const mockData = {
    transactions: [
        {
            id: 1,
            amount: { USD: 150.75, CDF: 376875 },
            caissier: 'Jean Dupont',
            caisse: 'CP001',
            status: 'success',
            method: 'QR Code',
            client: 'Client Premium',
            timestamp: new Date(Date.now() - 300000)
        },
        {
            id: 2,
            amount: { USD: 89.50, CDF: 223750 },
            caissier: 'Marie Martin',
            caisse: 'CS002',
            status: 'success',
            method: 'Mobile Money',
            client: 'Particulier',
            timestamp: new Date(Date.now() - 600000)
        }
    ],
    caissiers: [
        {
            name: 'Jean Dupont',
            transactions: 42,
            amount: { USD: 2845.50, CDF: 7113750 },
            performance: 95,
            avatar: '👑',
            trend: 'up'
        }
    ],
    alerts: [
        {
            id: 1,
            type: 'warning',
            title: 'Caisse CE003 inactive',
            message: 'La caisse Express n\'a pas été utilisée depuis 24h',
            timestamp: new Date(Date.now() - 7200000),
            priority: 'medium'
        }
    ]
};

const salesData = {
    daily: {
        labels: ['06h', '08h', '10h', '12h', '14h', '16h', '18h', '20h'],
        values: [250, 450, 720, 1250, 980, 1650, 2100, 1840]
    },
    weekly: {
        labels: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
        values: [1250, 1845, 1520, 2100, 1950, 2400, 1800]
    },
    monthly: {
        labels: ['Sem1', 'Sem2', 'Sem3', 'Sem4'],
        values: [5200, 6800, 7200, 8500]
    }
};

const Dashboard = () => {
    const { selectedOrganization, user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({});
    const [apiDashboard, setApiDashboard] = useState({
        dailyAmount: 0,
        lastDayAmount: 0,
        monthlyAmount: 0,
        lastMonthAmount: 0,
        yearlyAmount: 0,
        lastYearAmount: 0
    });
    const [chartData, setChartData] = useState({ labels: [], values: [] });
    const chartYear = new Date().getFullYear();
    const [chartLoading, setChartLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currencyLoading, setCurrencyLoading] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentAccount, setCurrentAccount] = useState({ currency: 'CDF', balance: 0, incomingBalance: 0, outgoingBalance: 0, lastUpdate: null });
    const [loadingAccount, setLoadingAccount] = useState(false);
    const hasLoadedOnceRef = useRef(false);

    // Fonction de normalisation du rôle (convertit 'caissier(e)' en 'cashier')
    const normalizeRole = (roleName) => {
        if (!roleName) return 'admin';
        const normalized = roleName.toLowerCase().trim();
        // Normaliser les variantes de caissier vers 'cashier'
        if (normalized.includes('caissier') || normalized === 'cashier') {
            return 'cashier';
        }
        if (normalized.includes('admin') || normalized === 'administrator') {
            return 'admin';
        }
        return normalized;
    };

    // Rediriger les caissiers vers leur page dédiée
    useEffect(() => {
        if (selectedOrganization) {
            // Vérifier le rôle sélectionné (priorité) ou le rôle de l'organisation
            const rawRole = selectedOrganization.selectedRole || selectedOrganization.role;
            const role = normalizeRole(rawRole);
            
            console.log('[Dashboard] Vérification du rôle pour redirection:', {
                selectedOrganizationData: {
                    id: selectedOrganization.id,
                    name: selectedOrganization.name,
                    role: selectedOrganization.role,
                    selectedRole: selectedOrganization.selectedRole
                },
                rawRole,
                normalizedRole: role,
                willRedirect: role === 'cashier'
            });

            if (role === 'cashier') {
                console.log('[Dashboard] Redirection vers /cashier car rôle = caissier (normalisé)');
                navigate('/cashier', { replace: true });
            }
        }
    }, [selectedOrganization, navigate]);

    const loadDashboardData = useCallback(async ({ silent = false } = {}) => {
        if (!selectedOrganization) {
            setLoading(false);
            setCurrencyLoading(false);
            return;
        }

        try {
            if (silent) {
                setCurrencyLoading(true);
            } else {
                setLoading(true);
            }

            const solutionId = localStorage.getItem(storageKeys.solution) || localStorage.getItem('netikash_solution');
            const own = selectedOrganization.id;
            const isAdmin = normalizeRole(selectedOrganization?.selectedRole || selectedOrganization?.role) === 'admin';

            // API dashboard (comme sur mobile): montants jour / mois / année
            try {
                const dashboardRes = await fetchDashboardStats({
                    own,
                    sol: solutionId,
                    srv: 'all',
                    act: isAdmin ? 'all' : (user?.id || 'all'),
                    crc: currency,
                    pvd: 'all'
                });
                setApiDashboard({
                    dailyAmount: dashboardRes.dailyAmount ?? 0,
                    lastDayAmount: dashboardRes.lastDayAmount ?? 0,
                    monthlyAmount: dashboardRes.monthlyAmount ?? 0,
                    lastMonthAmount: dashboardRes.lastMonthAmount ?? 0,
                    yearlyAmount: dashboardRes.yearlyAmount ?? 0,
                    lastYearAmount: dashboardRes.lastYearAmount ?? 0
                });
            } catch (e) {
                setApiDashboard({ dailyAmount: 0, lastDayAmount: 0, monthlyAmount: 0, lastMonthAmount: 0, yearlyAmount: 0, lastYearAmount: 0 });
            }
            
            // Calculer les dates selon la période
            const now = new Date();
            let startDate = new Date();
            
            if (timeRange === 'today') {
                startDate.setHours(0, 0, 0, 0);
            } else if (timeRange === 'week') {
                startDate.setDate(now.getDate() - 7);
            } else if (timeRange === 'month') {
                startDate.setMonth(now.getMonth() - 1);
            }
            
            // Charger les transactions (solutionId déjà défini plus haut)
            const transactionParams = {
                own: selectedOrganization.id, // ID de l'organisation directement
                sol: solutionId,
                srv: 'all', // Service (srv|| "all" dans le mobile)
                act: 'all', // Acteur (all pour admin, user.id pour caissier)
                ref: 'all', // Référence
                opr: 'all', // Opération
                slug: '', // Slug (envoyé même si vide dans le mobile)
                crc: currency, // Devise
                sts: 'all', // Statut
                lmt: 1000,
                pge: 1
            };
            
            console.log('[Dashboard] Organisation ID:', selectedOrganization.id);
            console.log('[Dashboard] Solution ID:', solutionId);
            console.log('[Dashboard] Paramètres de transaction:', transactionParams);
            
            // Essayer de récupérer les transactions, mais continuer même en cas d'erreur
            let transactions = [];
            try {
                const transactionsResponse = await fetchTransactions(transactionParams);
                console.log('[Dashboard] Réponse transactions:', {
                    total: transactionsResponse.contents?.length || 0,
                    sample: transactionsResponse.contents?.[0] || null
                });
                transactions = transactionsResponse.contents || [];
            } catch (error) {
                console.warn('[Dashboard] Erreur lors de la récupération des transactions:', error);
                console.warn('[Dashboard] Continuation avec un tableau vide de transactions');
                // Continuer avec transactions = [] pour afficher au moins les stats basiques
            }
            
            // Charger les POS
            const posList = await fetchCompanyPos(selectedOrganization.id);
            console.log('[Dashboard] POS récupérés:', posList.length, posList);
            
            // Calculer les statistiques
            const completedTransactions = transactions.filter(t => 
                t.status === 'completed' || t.status === 'COMPLETED'
            );
            
            const failedTransactions = transactions.filter(t => 
                t.status === 'failed' || t.status === 'FAILED' || t.status === 'canceled'
            );

            const totalAmountUSD = completedTransactions
                .filter(t => t.currency === 'USD')
                .reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0);
            
            const totalAmountCDF = completedTransactions
                .filter(t => t.currency === 'CDF')
                .reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0);

            const conversionRate = transactions.length > 0 
                ? ((completedTransactions.length / transactions.length) * 100).toFixed(1)
                : 0;

            const averageUSD = completedTransactions.filter(t => t.currency === 'USD').length > 0
                ? totalAmountUSD / completedTransactions.filter(t => t.currency === 'USD').length
                : 0;
            
            const averageCDF = completedTransactions.filter(t => t.currency === 'CDF').length > 0
                ? totalAmountCDF / completedTransactions.filter(t => t.currency === 'CDF').length
                : 0;

            // Graphique "Performance des Ventes" : chargé uniquement via API /trs/transactions/chart (comme mobile), pas ici


            // Stats finales
            const finalStats = {
                totalTransactions: transactions.length,
                successfulTransactions: completedTransactions.length,
                failedTransactions: failedTransactions.length,
                totalAmount: { USD: totalAmountUSD, CDF: totalAmountCDF },
                averageTransaction: { USD: averageUSD, CDF: averageCDF },
                activeCaisses: posList.length,
                activeClients: new Set(transactions.map(t => t.buyer?.account).filter(Boolean)).size,
                conversionRate: parseFloat(conversionRate)
            };
            
            console.log('[Dashboard] Statistiques calculées:', finalStats);
            setStats(finalStats);

        } catch (error) {
            console.error('[Dashboard] Erreur lors du chargement des données:', error);

            try {
                const posList = await fetchCompanyPos(selectedOrganization.id).catch(() => []);
                setStats({
                    totalTransactions: 0,
                    successfulTransactions: 0,
                    failedTransactions: 0,
                    totalAmount: { USD: 0, CDF: 0 },
                    averageTransaction: { USD: 0, CDF: 0 },
                    activeCaisses: posList.length,
                    activeClients: 0,
                    conversionRate: 0
                });
            } catch (fallbackError) {
                console.error('[Dashboard] Erreur même lors de la récupération basique:', fallbackError);
                setStats({
                    totalTransactions: 0,
                    successfulTransactions: 0,
                    failedTransactions: 0,
                    totalAmount: { USD: 0, CDF: 0 },
                    averageTransaction: { USD: 0, CDF: 0 },
                    activeCaisses: 0,
                    activeClients: 0,
                    conversionRate: 0
                });
            }
                    } finally {
            if (silent) {
                setCurrencyLoading(false);
            } else {
                setLoading(false);
            }
            hasLoadedOnceRef.current = true;
        }
    }, [timeRange, currency, selectedOrganization]);

    // Compte (Solde bloqué + Balance principale) — comme sur mobile /trs/accounts
    useEffect(() => {
        if (!currency || !selectedOrganization?.id) return;
        const loadAccount = async () => {
            setLoadingAccount(true);
            try {
                const sol = localStorage.getItem(storageKeys.solution) || localStorage.getItem('netikash_solution');
                const data = await fetchAccountDetails({ sol: sol || '', crc: currency, own: selectedOrganization.id });
                setCurrentAccount({
                    currency: data?.currency || currency,
                    balance: data?.balance ?? 0,
                    incomingBalance: data?.incomingBalance ?? 0,
                    outgoingBalance: data?.outgoingBalance ?? 0,
                    lastUpdate: data?.lastUpdate ?? null
                });
            } catch (e) {
                setCurrentAccount(prev => ({ ...prev, currency, balance: 0, incomingBalance: 0, outgoingBalance: 0 }));
            } finally {
                setLoadingAccount(false);
            }
        };
        loadAccount();
    }, [currency, selectedOrganization?.id]);

    // Écouter les événements de création de transaction pour actualiser le dashboard
    useEffect(() => {
        const handlePaymentRequestCreated = () => {
            // Recharger les données du dashboard
            loadDashboardData({ silent: true });
        };

        window.addEventListener('paymentRequest:created', handlePaymentRequestCreated);
        window.addEventListener('transaction:created', handlePaymentRequestCreated);
        
        return () => {
            window.removeEventListener('paymentRequest:created', handlePaymentRequestCreated);
            window.removeEventListener('transaction:created', handlePaymentRequestCreated);
        };
    }, [loadDashboardData]);

    // Chargement initial + changement d'organisation
    useEffect(() => {
        if (!selectedOrganization?.id) return;
        loadDashboardData({ silent: hasLoadedOnceRef.current });
    }, [selectedOrganization?.id, loadDashboardData]);

    // Refresh silencieux sur changement de période/devise (sans écran de chargement)
    useEffect(() => {
        if (!selectedOrganization?.id) return;
        if (!hasLoadedOnceRef.current) return;
        loadDashboardData({ silent: true });
    }, [timeRange, currency, selectedOrganization?.id, loadDashboardData]);

    // Graphique dynamique (API comme sur mobile) : /trs/transactions/chart
    useEffect(() => {
        if (!selectedOrganization?.id) return;
        const sol = localStorage.getItem(storageKeys.solution) || localStorage.getItem('netikash_solution');
        setChartLoading(true);
        fetchTransactionChart({
            own: selectedOrganization.id,
            sol: sol || '',
            crc: currency,
            yar: chartYear
        })
            .then((data) => {
                const labels = data?.labels || [];
                const values = (data?.datasets?.[0]?.data || []).map((d) => (d == null ? 0 : Number(d)));
                setChartData({ labels, values });
            })
            .catch(() => setChartData({ labels: [], values: [] }))
            .finally(() => setChartLoading(false));
    }, [selectedOrganization?.id, currency, chartYear]);



    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse shadow-lg">
                            <TrendingUp className="text-white text-3xl" />
                        </div>
                        <div className="absolute inset-0 border-2 border-amber-300 rounded-xl animate-ping opacity-20"></div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Chargement du tableau de bord</h3>
                    <p className="text-gray-600">Préparation des données en temps réel...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="dashboard"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen p-4 lg:p-6">
                    <div className="fixed inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-amber-500/5 rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-orange-500/5 rounded-full blur-3xl"></div>
                    </div>

                    <div className="relative z-10 space-y-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            currencyLoading={currencyLoading}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        <AdminStatsGrid
                            stats={stats}
                            apiDashboard={apiDashboard}
                            currency={currency}
                            timeRange={timeRange}
                            loading={loading}
                        />

                        {/* Fonds en attente + Balance principale */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                            <BlockedBalanceCard
                                account={currentAccount}
                                currency={currency}
                                loading={loadingAccount}
                            />

                            {/* Carte — Balance totale */}
                            <div className="relative overflow-hidden rounded-2xl border-2 border-amber-200/80 bg-gradient-to-br from-white via-amber-50/40 to-orange-50/30 shadow-xl shadow-amber-500/10 h-full flex flex-col">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-2xl pointer-events-none" />
                                <div className="relative z-10 p-5 lg:p-6 flex flex-col flex-1">
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                                                <TrendingUp className="text-white text-2xl" />
                                            </div>
                                            <div>
                                                <p className="text-amber-800/70 text-[10px] font-bold uppercase tracking-widest">
                                                    Disponible
                                                </p>
                                                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                                                    Balance totale
                                                </h2>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-slate-500 text-right shrink-0 max-w-[8rem] leading-tight">
                                            MAJ{' '}
                                            {currentAccount?.lastUpdate
                                                ? new Date(currentAccount.lastUpdate).toLocaleDateString('fr-FR', {
                                                    day: '2-digit',
                                                    month: 'short'
                                                })
                                                : '—'}
                                        </p>
                                    </div>
                                    <div className="mt-auto rounded-xl bg-white/80 border border-amber-100 px-4 py-4 lg:py-5">
                                        <p className="text-3xl lg:text-4xl font-bold text-slate-900 tabular-nums leading-tight">
                                            {loadingAccount ? (
                                                <span className="inline-block w-32 h-10 bg-slate-200 rounded animate-pulse" />
                                            ) : (
                                                <>
                                                    <span className="text-lg lg:text-xl text-amber-700 font-semibold mr-1">
                                                        {(currentAccount?.currency || currency) === 'CDF' ? 'CDF' : '$'}
                                                    </span>
                                                    {Number(currentAccount?.balance ?? 0).toLocaleString('fr-FR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </>
                                            )}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-2">
                                            Solde principal de votre compte organisation
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            <AdminSalesChart
                                data={chartData}
                                currency={currency}
                                loading={chartLoading}
                                apiDashboard={apiDashboard}
                                chartYear={chartYear}
                            />
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;